/**
 * OAuth Callback Page
 *
 * Handles the redirect from social media platforms after OAuth authorization.
 */
import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Spin, Result, Typography } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

const { Text } = Typography

export default function SocialMediaCallbackPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')
  // Prevent double execution in React StrictMode
  const hasProcessed = useRef(false)

  useEffect(() => {
    // Skip if already processed (React StrictMode runs effects twice)
    if (hasProcessed.current) return
    hasProcessed.current = true

    const handleCallback = async () => {
      const code = searchParams.get('code')
      const state = searchParams.get('state')
      const error = searchParams.get('error')

      // Check for OAuth error
      if (error) {
        setStatus('error')
        setErrorMessage(searchParams.get('error_description') || error)
        return
      }

      // Validate required params
      if (!code || !state) {
        setStatus('error')
        setErrorMessage('Missing authorization code or state')
        return
      }

      // Extract platform from state (format: PLATFORM_random_timestamp)
      const platform = state.split('_')[0]
      if (!platform) {
        setStatus('error')
        setErrorMessage('Invalid state parameter')
        return
      }

      try {
        const token = localStorage.getItem('admin_access_token')
        const redirectUri = `${window.location.origin}/social-media/accounts/callback`

        const response = await fetch(
          `${API_URL}/admin/social-media/accounts/${platform}/callback`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              code,
              redirectUri,
              state,
            }),
          }
        )

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.message || 'Failed to connect account')
        }

        setStatus('success')

        // Redirect to accounts page after 2 seconds
        setTimeout(() => {
          navigate('/social-media/accounts')
        }, 2000)
      } catch (err) {
        setStatus('error')
        setErrorMessage(err instanceof Error ? err.message : 'Unknown error occurred')
      }
    }

    handleCallback()
  }, [searchParams, navigate])

  if (status === 'loading') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: 16
      }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        <Text>Povezivanje naloga...</Text>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <Result
        status="error"
        title="Greska pri povezivanju"
        subTitle={errorMessage}
        extra={
          <button
            onClick={() => navigate('/social-media/accounts')}
            style={{
              padding: '8px 16px',
              background: '#1e3a5f',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer'
            }}
          >
            Nazad na naloge
          </button>
        }
      />
    )
  }

  return (
    <Result
      status="success"
      title="Nalog je uspesno povezan!"
      subTitle="Preusmeravanje na stranicu naloga..."
    />
  )
}
